/**
 * Device wire types — sv_control's DeviceSummary / DeviceDetail as sv_gateway
 * serializes them (snake_case, int64 → number, google.protobuf.*Value → value or
 * null). 1:1 with the wire.
 *
 * DeviceDetail embeds an access-log list and a personnel list, so it borrows those
 * two record types from their own entities (type-only imports — no runtime coupling).
 */
import type {AccessLogRecord} from '@/entities/log/types';
import type {PersonnelRecord} from '@/entities/personnel/types';

/** The list envelope's `meta` block (page is 1-based). */
export type PageMeta = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

/** Gate direction a terminal enforces. */
export type GateMode = 'entry' | 'exit' | 'both';

/**
 * What a row in the device list actually IS (added 2026-08-11).
 *
 * The list stopped being a list of identical things some time before it said so:
 * cameras reported by a video box have always arrived as ordinary device rows, and
 * nothing but the name distinguished one from a terminal. `device_type` is that
 * distinction, and `include_desktops` on the list route folds in the desktop bridges
 * that used to need `/control/desktops` and a screen of their own.
 *
 * Rows written before the field existed default to `faceid_terminal`, so the field is
 * always present — but the vocabulary is the SERVER'S and may grow. Anything
 * unrecognised must still render (see `deviceTypeLabelKey`), and none of these values
 * may reach a user unlabelled: they are machine tokens.
 */
export const DEVICE_TYPES = [
  'faceid_terminal',
  'camera',
  'nvr',
  'ptz_camera',
  'barrier',
  'controller',
  'desktop',
  'other',
] as const;

export type DeviceType = (typeof DEVICE_TYPES)[number];

/** The i18n key for a device type, falling back to `other` for a value we don't know. */
export function deviceTypeLabelKey(value: string | null | undefined): string {
  return (DEVICE_TYPES as readonly string[]).includes(value ?? '')
    ? `deviceType.${value}`
    : 'deviceType.other';
}

/**
 * True for the kinds that are a FaceID terminal in all but name — the ones a gate
 * mode, a settings profile and a personnel push mean something for. A camera has no
 * door to open, and a desktop bridge is not standing at one.
 */
export function isTerminalLike(value: string | null | undefined): boolean {
  return value === 'faceid_terminal' || value === undefined || value === null || value === '';
}

/** True for the kinds a live picture can be asked of (docs/api/cameras/live-video.md §3.1). */
export function isCameraLike(value: string | null | undefined): boolean {
  return value === 'camera' || value === 'ptz_camera' || value === 'nvr';
}

/**
 * The glyph for a device kind. Four marks rather than eight: a barrier and a
 * controller are both boxes on the wall next to the door, and drawing them apart
 * would add two glyphs nobody can tell apart at 21pt.
 */
export function deviceTypeIcon(
  value: string | null | undefined,
): 'devices' | 'camera' | 'desktop' {
  if (isCameraLike(value)) {
    return 'camera';
  }
  return value === 'desktop' ? 'desktop' : 'devices';
}

/** Live hardware snapshot (relay/LED/camera) as of the terminal's last sync. */
export type HalStatus = {
  relay_on: boolean;
  led_red: boolean;
  led_green: boolean;
  led_blue: boolean;
  led_ir: boolean;
  white_percent: number;
  camera_source: string;
  backend: string;
};

/** One remote-control command's lifecycle (management view). */
export type DeviceCommandRecord = {
  id: string;
  device_id: string;
  kind: string;
  params_json: string;
  /** pending | delivered | acked | failed */
  status: string;
  result_message: string;
  created_at: string;
  delivered_at?: string | null;
  acked_at?: string | null;
};

/** GET /control/devices/{id}/commands envelope. */
export type DeviceCommandList = {
  commands: DeviceCommandRecord[];
};

/**
 * One terminal account as the cloud mirrors it. No password, no hash — the
 * board is the source of truth and only ever reports names and roles.
 */
export type DeviceAccount = {
  username: string;
  /** `admin` | `operator`. A string, as it is on the wire and in the board's SQLite. */
  role: string;
  /** When the board last changed this account, ms since epoch. */
  updated_at_millis: number;
};

export type DeviceSummary = {
  device_id: string;
  device_name: string;
  /**
   * `SVFID-XXXX-XXXX-XXXX` — the number printed on the terminal's own screen, the
   * one a person quotes to support and the only thing that survives a rename.
   * sv_control keeps it from the sync round (`machineNumber`), but the gateway's
   * `DeviceSummary` does not serialise it yet (CABINET-BACKEND-ASKS.md, §2), so it
   * is optional here and the screens draw it only when it arrives.
   */
  machine_number?: string | null;
  /**
   * Always present since 2026-08-11; typed as a plain string because the server owns
   * the vocabulary and a narrow union here would turn a new kind into a type error
   * instead of an unfamiliar badge. Narrow with `deviceTypeLabelKey`/`isTerminalLike`.
   */
  device_type: string;
  location_label: string;
  department_id?: string | null;
  department_name?: string | null;
  ip_address?: string | null;
  /** IPv6 (глобальный или ULA) в каноническом виде; null — нет или прошивка старше. */
  ipv6_address?: string | null;
  /** `AA:BB:CC:DD:EE:FF`; null — устройство не сообщало. */
  mac_address?: string | null;
  manufacturer: string;
  model: string;
  android_version: string;
  app_version: string;
  /**
   * The installed system image, read from `/etc/samvision-firmware` on the board and
   * reported every sync round since firmware 2026-08-20. A separate track from
   * `app_version` — the two never match. Absent/empty means UNKNOWN (older boards say
   * nothing), not "outdated".
   */
  firmware_version?: string | null;
  /**
   * The update channel the board follows, per track (since 2026-09-08):
   * `update_channel` for the application, `firmware_channel` for the image. The
   * board installs only the newest build of ITS channel and refuses a pinned
   * version that is not that build — so the panel compares against this channel,
   * never a fixed one. Absent reads as `stable`, the board's own default.
   */
  update_channel?: string | null;
  firmware_channel?: string | null;
  personnel_count: number;
  recent_log_count: number;
  network_connected: boolean;
  sync_enabled: boolean;
  last_sync_at?: string | null;
  last_seen_at?: string | null;
  lan_reachable: boolean;
  online: boolean;
  pending_settings_sync: boolean;
  pending_personnel_sync: boolean;
  pending_full_sync: boolean;
  personnel_mode: string;
  assigned_profile_id?: string | null;
  /** Free-form on the summary; narrow to GateMode only when driving the switch. */
  gate_mode: string;
  /** Live hardware snapshot as of last sync, or null if never reported. */
  hal_status?: HalStatus | null;
  /**
   * What the board has left of itself, reported every sync round since firmware
   * 2026-08-20.
   *
   * ⚠️ `null` is "this firmware does not report it", NOT zero. Older builds send
   * nothing, and drawing that as 0 would declare a healthy terminal out of memory —
   * on rollout day the whole fleet would light up. Show a dash, and never colour it.
   *
   * `available_memory_kb` is `MemAvailable`, not free memory: Linux keeps a cache it
   * hands back on demand, so free memory is always small and an alarm on it would be
   * an alarm that is always on.
   *
   * `db_bytes` counts the database TOGETHER with its WAL and SHM. That is the point of
   * the number — a WAL that stops folding back grew larger than the database itself on
   * a live board (4.2 MB against 3.3), and a figure for the database alone reads
   * "small, all fine" right up to the day the board runs out of space.
   */
  available_memory_kb?: number | null;
  /** Size of the orchestrator's log. It lives in `/tmp`, so this is memory too. */
  log_bytes?: number | null;
  db_bytes?: number | null;
  /**
   * How the terminal protects itself, as of its last sync.
   *
   * Absent means UNKNOWN, not "off": firmware older than 2026-08-15 (and the
   * Android terminal) never reports it. Drawing silence as `false` would tell
   * the owner a device is unprotected when nobody has asked it yet.
   */
  access_activated?: boolean | null;
  /**
   * `accounts` | `legacy_codes`. The latter is the emergency rollback to the
   * old, universally-known numeric code — worth saying out loud.
   */
  access_menu_mode?: string | null;
  access_desktop_guarded?: boolean | null;
  /**
   * The terminal's own accounts.
   *
   * `repeated` on the wire, so an empty list is indistinguishable between "old
   * firmware" and "a board with no accounts" — `access_activated` is what
   * answers that, and the card keys its empty state off that field, not off
   * this one.
   */
  access_accounts?: DeviceAccount[];
  /**
   * Who owns this terminal's settings, when it is not the cloud (SV-13).
   *
   * A terminal in «desktop» mode reaches the cloud only through that desktop's relay:
   * settings, profile, gate mode, commands and force-sync written here never reach the
   * door, and the server answers them `409`. `null`/absent — the cloud is the owner.
   * An object rather than a flag so that reading and editing THROUGH the desktop can be
   * added later without changing the shape.
   */
  managed_by?: DeviceManager | null;
};

export type DeviceManager = {
  /** Only `desktop` today. */
  kind: string;
  device_id: string;
  /** Empty when the desktop's own row is gone. */
  name: string;
};

/** The desktop that owns this terminal's settings, or null when the cloud does. */
export function managingDesktop(s: Pick<DeviceSummary, 'managed_by'>): DeviceManager | null {
  return s.managed_by?.kind === 'desktop' ? s.managed_by : null;
}

/**
 * Effective terminal settings embedded in DeviceDetail — the snake_case proto shape
 * sv_gateway serializes. The trailing block (nfc_timeout_ms … gpu_acceleration) mirrors
 * the terminal's DeviceConfig/AiConfig; editing them via saveDeviceSettings writes a
 * per-device override that the terminal pulls and hot-applies on its next sync.
 */
export type TerminalSettings = {
  company_name: string;
  admin_password: string;
  default_lens_facing: string;
  monochrome_enabled: boolean;
  preview_rotation_degrees: number;
  recognition_cooldown_seconds: number;
  recognition_accuracy_percent: number;
  photo_liveness_enabled: boolean;
  kiosk_mode_enabled: boolean;
  door_open_seconds: number;
  app_language: string;
  company_logo_base64: string;
  director_telegram_id: string;
  nfc_timeout_ms: number;
  camera_source: string; // "rgb" | "ir"
  deny_led_red: boolean;
  audio_enabled: boolean;
  log_denials: boolean;
  confirm_frames: number;
  spoof_detection: boolean;
  liveness_enabled: boolean; // motion liveness, distinct from photo_liveness_enabled
  flat_search: boolean;
  gpu_acceleration: boolean;
};

/** A named settings preset (sv_control SettingsProfile) as the BFF serializes it. */
export type SettingsProfile = {
  profile_id: string;
  company_id: string;
  name: string;
  description: string;
  payload: TerminalSettings;
  revision: number;
  is_global_default: boolean;
  created_at: string;
  updated_at: string;
  device_count: number;
};

/** GET /control/profiles envelope (sv_control's ListProfilesResponse — not meta/data). */
export type PagedProfiles = {
  profiles: SettingsProfile[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
};

export type SyncHistoryRecord = {
  sync_id: string;
  device_id: string;
  protocol_version: number;
  reason: string;
  reported_settings_hash?: string | null;
  reported_personnel_hash?: string | null;
  logs_received: number;
  settings_received: boolean;
  personnel_received: boolean;
  settings_returned: boolean;
  personnel_returned: boolean;
  status: string;
  received_at: string;
  completed_at: string;
  message: string;
};

/** GET /control/devices/{device_id}. */
export type DeviceDetail = {
  summary: DeviceSummary;
  effective_settings: TerminalSettings;
  assigned_profile_id?: string | null;
  has_device_override: boolean;
  recent_logs: AccessLogRecord[];
  sync_history: SyncHistoryRecord[];
  assigned_personnel_count: number;
  assigned_personnel: PersonnelRecord[];
};

/** GET /control/devices envelope. */
export type PagedDevices = {
  meta: PageMeta;
  data: DeviceSummary[];
};
